import React, { useState, cloneElement, lazy } from 'react';
import { TrendingUpIcon, UsersIcon, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { ImageData } from '../utils/mockData';
import { motion } from 'framer-motion';
import { DemographicCharts } from './DemographicCharts';
import { ImagePerformanceTable } from './ImagePerformanceTable';
interface MetricsOverviewProps {
  data: ImageData[];
  demographicData?: {
    genderData: {
      male: number;
      female: number;
      other: number;
    };
    ageData: {
      '18-24': number;
      '25-34': number;
      '35-44': number;
      '45-54': number;
      '55-64': number;
      '65+': number;
      unknown: number;
    };
  };
}
const ComparisonMetric: React.FC<{
  value: number;
  comparisonValue: number;
  label: string;
}> = ({
  value,
  comparisonValue,
  label
}) => {
  const isHigher = value > comparisonValue;
  return <div className={`text-xs flex items-center ${isHigher ? 'text-[#D1005D]' : 'text-[#707070]'}`} data-id="element-256" data-mp="28896l46vw36x">
      <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${isHigher ? 'bg-[#D1005D]' : 'bg-gray-300'}`} data-id="element-257" data-mp="2hb98rir3bo8" />
      <span data-id="element-258" data-mp="7fmjh3cyjo6z">
        VS {comparisonValue.toLocaleString()} {label}
      </span>
    </div>;
};
export const MetricsOverview: React.FC<MetricsOverviewProps> = ({
  data,
  demographicData
}) => {
  const totalSignals = data.reduce((sum, item) => sum + item.views, 0);
  const bestPerformer = [...data].sort((a, b) => b.stoppingPower - a.stoppingPower)[0];
  const metrics = [{
    title: 'Best Performing Asset',
    value: <div className="flex items-center space-x-3" data-id="element-259" data-mp="qd7581kfe6nd">
          <div className="w-12 h-12 rounded-sm overflow-hidden bg-[#F9F9FB]" data-id="element-260" data-mp="v71lsw4qi5i8">
            <img src={bestPerformer.thumbnailUrl} alt={bestPerformer.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" loading="lazy" data-id="element-261" data-mp="105lav5emjxet" />
          </div>
          <div className="text-sm" data-id="element-262" data-mp="193ow00lp6s63">
            <p className="font-medium text-[#141617]" data-id="element-263" data-mp="358mrqovy7gky">{bestPerformer.name}</p>
            <p className="text-[#707070]" data-id="element-264" data-mp="356jsrfctid43">
              {bestPerformer.stoppingPower} SPR Scores
            </p>
          </div>
        </div>,
    icon: <TrendingUpIcon className="h-5 w-5" data-id="element-265" data-mp="3ezhj27o1x6d9" />,
    highlight: true
  }, {
    title: 'Human Audience Signals',
    value: '31,000',
    icon: <UsersIcon className="h-5 w-5" data-id="element-266" data-mp="3owvm95kyrynm" />,
    change: 'Combined reach',
    comparison: 'bought audience signals'
  }, {
    title: 'Confidence Level',
    value: '95%',
    icon: <ShieldCheckIcon className="h-5 w-5" data-id="element-267" data-mp="3k055sh58z11o" />,
    change: 'based on Chi-Square Analysis',
    comparison: 'bought confidence level'
  }];
  return <div className="space-y-6" data-id="element-268" data-mp="3szs9j9lg8ixw">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-269" data-mp="3swe57vt85znt">
        {metrics.map((metric, index) => <div key={index} className={`bg-white p-6 rounded-sm shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-[#F2F3F5] transition-shadow ${metric.highlight ? 'ring-1 ring-[#D1005D]/20' : ''}`} data-id="element-270" data-mp="23ldp31hgri87" data-mpi={index}>
            <div className="flex items-center mb-3" data-id="element-271" data-mp="j21lwewz5vcn">
              {cloneElement(metric.icon as React.ReactElement, {
            className: `h-5 w-5 ${metric.highlight ? 'text-[#D1005D]' : 'text-[#707070]'}`
          })}
              <h3 className="text-sm font-medium text-[#141617] ml-2" data-id="element-272" data-mp="9a5j4qjy1h23">
                {metric.title}
              </h3>
            </div>
            <div className="flex flex-col space-y-2" data-id="element-273" data-mp="b0lrnk4btri">
              <div className="flex items-baseline" data-id="element-274" data-mp="btjy050hay1">
                {typeof metric.value === 'string' ? <p className={`text-2xl font-semibold ${metric.highlight ? 'text-[#D1005D]' : 'text-[#141617]'}`} data-id="element-275" data-mp="3ii7xvz3a5kwf">
                    {metric.value}
                  </p> : metric.value}
                {metric.change && <span className="ml-2 text-xs font-medium text-gray-500" data-id="element-276" data-mp="1kore16o527le">
                    {metric.change}
                  </span>}
              </div>
              {metric.comparison && <ComparisonMetric value={metric.title === 'Human Audience Signals' ? 31000 : metric.title === 'Confidence Level' ? 95 : 0} comparisonValue={metric.title === 'Human Audience Signals' ? 30000 : 80} label={metric.comparison} data-id="element-277" data-mp="m21p5ymbxtw2" />}
            </div>
          </div>)}
      </div>
      {demographicData && <DemographicCharts genderData={demographicData.genderData} ageData={demographicData.ageData} data-id="element-278" data-mp="3ufqt774p4y18" />}
      <div data-id="element-279" data-mp="3lgn8xztsmcl5">
        <div className="flex items-center mb-4" data-id="element-280" data-mp="3lgwzppmnwnrw">
          <h2 className="text-lg font-medium text-white mr-3" data-id="element-281" data-mp="3lj9nwkj0jude">
            Asset Performance
          </h2>
          <div className="flex items-center bg-[#D1005D] text-white px-3 py-1.5 rounded-sm shadow-sm" data-id="element-282" data-mp="211gynhwpk6gg">
            <TrophyIcon className="h-4 w-4 mr-1.5" data-id="element-283" data-mp="29zt06ez44ono" />
            <span className="text-xs font-bold uppercase tracking-wide" data-id="element-284" data-mp="2a143kdonbue9">
              Winner: Red Background
            </span>
          </div>
        </div>
        <ImagePerformanceTable data={data} data-id="element-285" data-mp="1hbf3c2jrfydp" />
      </div>
    </div>;
};